import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, DollarSign, Edit2, Filter, Plus, Search, Target, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/common/FormSection';
import { FinanceStatusBadge } from '@/components/finance-v2/FinanceBadges';
import type { CostCenter } from '../types/financeV2.types';
import { financeV2Service, type CreateCostCenterDto, type UpdateCostCenterDto, type CostCenterFilters } from '../services/financeV2Service';
import { getErrorMessage } from '../utils/error.utils'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

function ErrorBox({ message }: { message: string }) {
  return (
    <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{message}</p>
  );
}

export function FinanceV2CostCentersPage() {
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CostCenterFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const loadCostCenters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.costCenters.list({ ...filters, page, limit: 20 });
      setCostCenters(response.data.costCenters);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar centros de custo'));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadCostCenters();
  }, [loadCostCenters]);

  const handleEdit = (costCenter: CostCenter) => {
    setSelectedCostCenter(costCenter);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este centro de custo?')) {
      return;
    }

    try {
      await financeV2Service.costCenters.delete(id);
      loadCostCenters();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao excluir centro de custo'));
    }
  };

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Target className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Centros de Custo</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie os centros de custo e orçamentos</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Novo Centro de Custo
        </Button>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-56">
            <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              aria-label="Buscar centros de custo"
              placeholder="Buscar centros de custo..."
              className="pl-8"
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            aria-controls="cost-center-filters-panel"
          >
            <Filter className="size-4" />
            Filtros
            {filters.isActive !== undefined && <Badge variant="secondary">1</Badge>}
          </Button>
          {Object.keys(filters).length > 0 && (
            <Button variant="ghost" type="button" onClick={clearFilters}>Limpar</Button>
          )}
        </div>

        {showFilters && (
          <Card size="sm" id="cost-center-filters-panel">
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField label="Status" htmlFor="cost-center-filter-status">
                <NativeSelect
                  id="cost-center-filter-status"
                  value={filters.isActive !== undefined ? filters.isActive.toString() : ''}
                  onChange={(e) => setFilters({ ...filters, isActive: e.target.value === '' ? undefined : e.target.value === 'true' })}
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </NativeSelect>
              </FormField>
            </CardContent>
          </Card>
        )}
      </div>

      {error && <ErrorBox message={error} />}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          <span className="sr-only">Carregando centros de custo...</span>
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : costCenters.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-6 text-center">
            <Target className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-base font-medium">Nenhum centro de custo encontrado</h3>
            <p className="text-sm text-muted-foreground">Comece criando um novo centro de custo</p>
            <Button className="mt-2" onClick={() => setShowCreateModal(true)}>
              <Plus className="size-4" /> Novo Centro de Custo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {costCenters.map((costCenter) => (
            <CostCenterCard
              key={costCenter.id}
              costCenter={costCenter}
              onEdit={() => handleEdit(costCenter)}
              onDelete={() => handleDelete(costCenter.id)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
              <ChevronLeft className="size-4" /> Anterior
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
              Próxima <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateCostCenterModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadCostCenters();
          }}
        />
      )}

      {showEditModal && selectedCostCenter && (
        <EditCostCenterModal
          costCenter={selectedCostCenter}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCostCenter(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedCostCenter(null);
            loadCostCenters();
          }}
        />
      )}
    </div>
  );
}

function CostCenterCard({ costCenter, onEdit, onDelete }: { costCenter: CostCenter; onEdit: () => void; onDelete: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Target className="size-5 text-primary" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h3 className="break-words font-medium">{costCenter.name}</h3>
              <p className="text-sm text-muted-foreground">{costCenter.code}</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Editar" className="text-muted-foreground" onClick={onEdit}>
                  <Edit2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Editar</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="Excluir" className="text-muted-foreground hover:text-destructive" onClick={onDelete}>
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Excluir</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {costCenter.description && (
          <p className="line-clamp-2 break-words text-sm text-muted-foreground">{costCenter.description}</p>
        )}

        {costCenter.budget ? (
          <div className="flex items-center gap-2 rounded-lg bg-muted p-3">
            <DollarSign className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Orçamento</p>
              <p className="text-sm font-medium tabular-nums">{formatCurrency(costCenter.budget)}</p>
            </div>
          </div>
        ) : null}

        <div className="border-t border-border pt-3">
          <FinanceStatusBadge status={costCenter.isActive ? 'Ativo' : 'Inativo'} />
        </div>
      </CardContent>
    </Card>
  );
}

function BudgetField({ id, value, onChange }: { id: string; value: number | undefined; onChange: (budget: number) => void }) {
  return (
    <FormField label="Orçamento" htmlFor={id}>
      <Input
        id={id}
        type="number"
        step="0.01"
        min="0"
        value={value || ''}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      />
    </FormField>
  );
}

function CreateCostCenterModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<CreateCostCenterDto>({
    name: '',
    code: '',
    description: '',
    budget: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await financeV2Service.costCenters.create(formData);
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar centro de custo'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Novo Centro de Custo</DialogTitle>
        </DialogHeader>

        {error && <ErrorBox message={error} />}

        <form id="create-cost-center-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <FormField label="Nome *" htmlFor="cost-center-name">
            <Input
              id="cost-center-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </FormField>

          <FormField label="Código *" htmlFor="cost-center-code">
            <Input
              id="cost-center-code"
              type="text"
              required
              placeholder="Ex: ADM, COM, OPR"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            />
          </FormField>

          <BudgetField id="cost-center-budget" value={formData.budget} onChange={(budget) => setFormData({ ...formData, budget })} />

          <FormField label="Descrição" htmlFor="cost-center-description">
            <Textarea
              id="cost-center-description"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </FormField>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" form="create-cost-center-form" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Centro de Custo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCostCenterModal({
  costCenter,
  onClose,
  onSuccess
}: {
  costCenter: CostCenter;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<UpdateCostCenterDto>({
    name: costCenter.name,
    description: costCenter.description || '',
    budget: costCenter.budget || 0,
    isActive: costCenter.isActive
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await financeV2Service.costCenters.update(costCenter.id, formData);
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao atualizar centro de custo'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !loading) onClose() }}>
      <DialogContent fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Editar Centro de Custo</DialogTitle>
        </DialogHeader>

        {error && <ErrorBox message={error} />}

        <form id="edit-cost-center-form" onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
          <FormField label="Nome *" htmlFor="edit-cost-center-name">
            <Input
              id="edit-cost-center-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </FormField>

          <FormField label="Código" htmlFor="edit-cost-center-code" hint="O código não pode ser alterado">
            <Input id="edit-cost-center-code" type="text" disabled value={costCenter.code} />
          </FormField>

          <BudgetField id="edit-cost-center-budget" value={formData.budget} onChange={(budget) => setFormData({ ...formData, budget })} />

          <FormField label="Descrição" htmlFor="edit-cost-center-description">
            <Textarea
              id="edit-cost-center-description"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </FormField>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="size-4 rounded-sm border-input accent-primary"
            />
            <Label htmlFor="isActive" className="font-normal">Centro de custo ativo</Label>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" form="edit-cost-center-form" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
